// This project has no tsconfig.json, so this reference is needed to pull in
// react-app-env.d.ts's ambient declarations (react-scripts types, *.css
// wildcard module) — an unimported .d.ts otherwise isn't part of this file's
// inferred TS project.
/// <reference path="../../../../react-app-env.d.ts" />
import React, { useEffect, useRef, useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import {
  Menu,
  Calendar,
  LayoutGrid,
  LayoutDashboard,
  BarChart3,
  Settings,
  House,
  User,
  LogOut,
  ChevronLeft,
  ChevronRight,
  X,
  Video,
} from 'lucide-react';
import { logoutUser } from '../../../../services/session';
import { useAuthSession } from '../../../../contexts/AuthSessionContext';
import { PRODUCT_IMAGES } from '../../../../branding/assets';
import '../styles/admin-layout.css';
import './Sidebar.css';

const MOBILE_BREAKPOINT = 768;
const AUTO_COLLAPSE_BREAKPOINT = 1100;

const shouldAutoCollapseSidebar = (width: number) => {
  return width >= MOBILE_BREAKPOINT && width <= AUTO_COLLAPSE_BREAKPOINT;
};

const navItems = [
  { to: '/reservo', label: 'Painel', icon: LayoutDashboard, end: true },
  { to: '/reservo/calendario', label: 'Reservas', icon: Calendar },
  { to: '/reservo/relatorios', label: 'Relatórios', icon: BarChart3 },
  { to: '/reservo/plantas', label: 'Ambientes', icon: LayoutGrid },
  { to: '/reservo/configuracoes', label: 'Configurações', icon: Settings },
];

const getIsDesktop = () =>
  typeof window === 'undefined' ? true : window.innerWidth >= MOBILE_BREAKPOINT;

const AdminLayout: React.FC = () => {
  const [isCollapsed, setIsCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return shouldAutoCollapseSidebar(window.innerWidth);
  });
  const [menuOpen, setMenuOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(getIsDesktop);
  const [tooltip, setTooltip] = useState({
    visible: false,
    text: '',
    top: 0,
    left: 0,
  });
  const [toggleHovered, setToggleHovered] = useState(false);
  const [backHovered, setBackHovered] = useState(false);
  const [logoutHovered, setLogoutHovered] = useState(false);
  const autoCollapseModeRef = useRef(
    typeof window !== 'undefined' && shouldAutoCollapseSidebar(window.innerWidth),
  );
  const navigate = useNavigate();
  const { user } = useAuthSession();

  const userName = user?.name || user?.username || 'Usuário';

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handleResize = () => {
      const width = window.innerWidth;
      const shouldCollapse = shouldAutoCollapseSidebar(width);
      setIsDesktop(width >= MOBILE_BREAKPOINT);

      if (width >= MOBILE_BREAKPOINT) {
        setMenuOpen(false);
      }

      if (shouldCollapse !== autoCollapseModeRef.current) {
        setIsCollapsed(shouldCollapse);
        autoCollapseModeRef.current = shouldCollapse;
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Reseta estados de hover quando o sidebar muda de visibilidade
  useEffect(() => {
    setTooltip((prev) => (prev.visible ? { ...prev, visible: false } : prev));
    setToggleHovered(false);
    setBackHovered(false);
    setLogoutHovered(false);
  }, [isCollapsed, menuOpen]);

  const handleToggle = () => {
    if (typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT) {
      setMenuOpen((prev) => !prev);
      return;
    }
    setIsCollapsed((prev) => !prev);
  };

  const handleToggleClick = () => {
    setTooltip((prev) => (prev.visible ? { ...prev, visible: false } : prev));
    setToggleHovered(false);
    handleToggle();
  };

  const handleBack = () => {
    navigate('/products');
  };

  const handleLogout = async () => {
    await logoutUser();
    navigate('/');
  };

  const mini = isDesktop ? isCollapsed : false;

  const handleTooltipShow = (label: string, target: HTMLElement | null) => {
    if (!mini || !target) return;
    const rect = target.getBoundingClientRect();
    setTooltip({
      visible: true,
      text: label,
      top: rect.top + rect.height / 2,
      left: rect.right + 12,
    });
  };

  const handleTooltipHide = () => {
    if (!tooltip.visible) return;
    setTooltip((prev) => ({ ...prev, visible: false }));
  };

  const toggleLabel = !isDesktop
    ? 'Fechar menu'
    : isCollapsed
      ? 'Expandir menu'
      : 'Recolher menu';

  return (
    <div className={`admin-layout ${menuOpen ? 'sidebar-open' : ''}`}>
      <div className={`sidebar ${mini ? 'collapsed' : ''} ${menuOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div
            className={`flex w-full items-center ${
              mini ? 'flex-col gap-2 px-1' : 'justify-between gap-2 px-3'
            }`}
            style={!mini ? { minHeight: '6rem' } : undefined}
          >
            {!mini && (
              <div
                className="relative shrink-0"
                onMouseEnter={() => setBackHovered(true)}
                onMouseLeave={() => setBackHovered(false)}
              >
                <button
                  type="button"
                  onClick={handleBack}
                  aria-label="Voltar para produtos"
                  className="flex h-11 w-11 items-center justify-center rounded-full bg-black/10 text-slate-900 ring-1 ring-black/20 transition-all hover:bg-black/20 hover:ring-black/40"
                >
                  <House size={22} strokeWidth={2.2} />
                </button>
                {backHovered && (
                  <span className="pointer-events-none absolute top-1/2 left-full ml-2 -translate-y-1/2 whitespace-nowrap rounded-md bg-slate-900/95 px-2.5 py-1 text-[11px] font-semibold text-white shadow-lg ring-1 ring-white/10">
                    Voltar para produtos
                  </span>
                )}
              </div>
            )}
            {!mini && (
              <img
                src={PRODUCT_IMAGES.reservo}
                alt="Reservo"
                className="min-w-0 shrink object-contain"
                style={{ height: '2.5rem', maxWidth: '110px' }}
              />
            )}
            {mini && (
              <button
                type="button"
                onClick={handleToggleClick}
                aria-label={toggleLabel}
                onMouseEnter={(event) => handleTooltipShow(toggleLabel, event.currentTarget)}
                onMouseLeave={handleTooltipHide}
                className="flex h-9 w-9 items-center justify-center rounded-full border border-black/25 bg-black/5 text-slate-900 transition-colors hover:bg-black/15"
              >
                <ChevronRight size={18} />
              </button>
            )}
            {!mini && (
              <div
                className="relative shrink-0"
                onMouseEnter={() => setToggleHovered(true)}
                onMouseLeave={() => setToggleHovered(false)}
              >
                <button
                  type="button"
                  onClick={handleToggleClick}
                  aria-label={toggleLabel}
                  className={`flex items-center justify-center rounded-full border border-black/25 bg-black/5 text-slate-900 transition-colors hover:bg-black/15 ${
                    !isDesktop ? 'h-11 w-11' : 'h-9 w-9'
                  }`}
                >
                  {!isDesktop ? <X size={22} /> : <ChevronLeft size={18} />}
                </button>
                {toggleHovered && isDesktop && (
                  <span className="pointer-events-none absolute top-1/2 right-full mr-2 -translate-y-1/2 whitespace-nowrap rounded-md bg-slate-900/95 px-2.5 py-1 text-[11px] font-semibold text-white shadow-lg ring-1 ring-white/10">
                    {toggleLabel}
                  </span>
                )}
              </div>
            )}
          </div>
          {mini && (
            <img
              src="/Reservo-icon.png"
              alt="Reservo"
              className="mt-3 h-10 w-auto self-center"
            />
          )}
        </div>
        <nav className="sidebar-nav">
          {navItems.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              aria-label={mini ? label : undefined}
              onClick={() => {
                setMenuOpen(false);
                handleTooltipHide();
              }}
              onMouseEnter={(event) => handleTooltipShow(label, event.currentTarget)}
              onMouseLeave={handleTooltipHide}
            >
              <Icon size={20} />
              {!mini && <span>{label}</span>}
            </NavLink>
          ))}
        </nav>
        <div className={mini ? 'mt-auto px-1 pb-2' : 'mt-auto px-3 pb-2'}>
          <NavLink
            to="/reservo/tutorial"
            aria-label={mini ? 'Tutorial' : undefined}
            onClick={() => {
              setMenuOpen(false);
              handleTooltipHide();
            }}
            onMouseEnter={(event) => handleTooltipShow('Tutorial', event.currentTarget)}
            onMouseLeave={handleTooltipHide}
            className={`flex items-center rounded-lg py-3 font-semibold text-slate-900 shadow-md ring-1 ring-black/10 transition-all hover:brightness-95 ${
              mini ? 'justify-center px-2' : 'gap-3 px-3'
            }`}
            style={{ backgroundColor: '#ffdb2f' }}
          >
            <Video size={20} />
            {!mini && <span>Tutorial</span>}
          </NavLink>
        </div>
        <div
          className={`sidebar-footer-user ${
            mini ? 'flex flex-col items-center px-1 py-3' : 'flex items-center gap-2 px-3 py-3'
          }`}
        >
          {!mini && (
            <>
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-600">
                <User size={18} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold text-slate-800 capitalize">
                  {userName}
                </div>
              </div>
            </>
          )}
          {mini ? (
            <button
              type="button"
              onClick={handleLogout}
              onMouseEnter={(event) => handleTooltipShow('Sair', event.currentTarget)}
              onMouseLeave={handleTooltipHide}
              className="flex h-10 w-10 items-center justify-center rounded-lg text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900"
              aria-label="Sair"
            >
              <LogOut size={18} />
            </button>
          ) : (
            <div
              className="relative shrink-0"
              onMouseEnter={() => setLogoutHovered(true)}
              onMouseLeave={() => setLogoutHovered(false)}
            >
              <button
                type="button"
                onClick={handleLogout}
                className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900"
                aria-label="Sair"
              >
                <LogOut size={18} />
              </button>
              {logoutHovered && (
                <span className="pointer-events-none absolute top-1/2 right-full mr-2 -translate-y-1/2 whitespace-nowrap rounded-md bg-slate-900/95 px-2.5 py-1 text-[11px] font-semibold text-white shadow-lg ring-1 ring-white/10">
                  Sair
                </span>
              )}
            </div>
          )}
        </div>
      </div>
      {!isDesktop && menuOpen && (
        <button
          type="button"
          aria-label="Fechar menu"
          onClick={() => setMenuOpen(false)}
          className="fixed inset-0 z-30 bg-black/40 md:hidden"
        />
      )}
      {!isDesktop && !menuOpen && (
        <button
          type="button"
          onClick={handleToggle}
          aria-label="Abrir menu"
          className="fixed top-3 left-3 z-50 flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-lg transition-all hover:bg-slate-50 active:scale-95 md:hidden"
        >
          <Menu size={22} />
        </button>
      )}
      <div className="admin-content">
        <main className="content">
          <Outlet />
        </main>
      </div>
      {mini && tooltip.visible && typeof document !== 'undefined' && createPortal(
        <div
          className="fixed px-3 py-1.5 rounded-md text-[11px] font-semibold tracking-wide bg-slate-900/95 text-white whitespace-nowrap z-[220] shadow-xl ring-1 ring-white/10 backdrop-blur-sm pointer-events-none"
          style={{ top: tooltip.top, left: tooltip.left, transform: 'translateY(-50%)' }}
        >
          {tooltip.text}
        </div>,
        document.body,
      )}
    </div>
  );
};

export default AdminLayout;
